import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'

/** How long typing has to pause before a search goes to the server. */
export const SEARCH_DELAY_MS = 300

/**
 * `value` once it has stopped changing for `delay` ms; an empty value at once, so that clearing
 * a search shows everything again without waiting.
 */
export function useDebouncedValue(value: string, delay = SEARCH_DELAY_MS): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), value === '' ? 0 : delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return value === '' ? '' : settled
}

/**
 * A search box. It is not part of a form and has no name, so that pressing Enter can never send
 * what was typed to an address: what a person searches for (a name, an email) stays in the page.
 */
export function SearchInput({
  value,
  onChange,
  label,
  placeholder,
  maxLength,
}: {
  value: string
  onChange: (value: string) => void
  label: string
  placeholder?: string
  maxLength?: number
}) {
  return (
    <label className="relative block min-w-0 flex-[2_1_16rem]">
      <span className="sr-only">{label}</span>
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        spellCheck={false}
        className="w-full rounded-lg border border-line bg-surface py-2 pr-3 pl-9 text-sm"
      />
    </label>
  )
}
